import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { PodStatus as PodStatusType } from '@/types/pods';
import { formatDistanceToNow, isToday, format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';

interface PodStatusProps {
	status?: PodStatusType;
	message?: string;
	timestamp?: string;
	urlsRan?: number;
	urls?: { url: string; platform: string }[];
	runCount?: number;
}

export function PodStatus({
	status,
	message,
	timestamp,
	urlsRan,
	urls,
	runCount,
}: PodStatusProps) {
	const [showUrls, setShowUrls] = useState(false);

	const getStatusColor = (status: string) => {
		switch (status?.trim()) {
			case 'INITIALIZING':
				return 'bg-purple-500';
			case '⚡️ ENGAGEMENT POD DONE':
				return 'bg-purple-700';
			case '🔌 SCRAPING DONE':
			case '🔌SCRAPING DONE':
			case 'SCRAPING DONE':
				return 'bg-sky-700';
			case '🔨 SCRAPING POST':
				return 'bg-sky-400';
			case '🔨 CREATING...':
				return 'bg-pink-500';
			case '😴 Sleeping...':
				return 'bg-indigo-400';
			case '✍️ Commenting...':
				return 'bg-blue-500';
			case '✅ COMMENTED':
				return 'bg-green-500';
			case '👀 Logging...':
				return 'bg-violet-500';
			case '🔑 LOGGED':
				return 'bg-violet-700';
			case '✍️ Replying...':
				return 'bg-blue-600';
			case '✅ REPLIED':
				return 'bg-green-600';
			case 'ERROR':
				return 'bg-red-500';
			case 'RATE_LIMITED':
				return 'bg-yellow-500';
			case '🎯 Starting Engagement Engine..':
				return 'bg-blue-400';
			case '🔍 Analyzing image...':
				return 'bg-indigo-500';
			case '✨ Image analysis scheduled':
				return 'bg-indigo-600';
			case '💭 Gathering context...':
				return 'bg-violet-500';
			case '✍️ Writing comment...':
				return 'bg-purple-500';
			case '⚡️ Comment scheduled':
				return 'bg-purple-600';
			case '💾 Saving results...':
				return 'bg-emerald-500';
			case '✅ Processing complete!':
				return 'bg-green-500';
			case '❌ Image analysis failed':
			case '❌ Comment generation failed':
			case '❌ Failed to save results':
				return 'bg-red-500';
			default:
				return 'bg-orange-500/90';
		}
	};

	const formatTimestamp = (timestamp: string) => {
		const date = new Date(timestamp);
		if (isToday(date)) {
			return `Today, ${formatDistanceToNow(date, { addSuffix: true })}`;
		}
		return format(date, "MMM d 'at' h:mm a");
	};

	const formatMessage = (message: string) => {
		if (!message) return '';

		// Split the message into time and run count parts
		const [timePart, runPart] = message.split(' - ');

		// Format the time part
		const timeNum = Number(timePart);
		let formattedTime = '';
		if (!isNaN(timeNum)) {
			const minutes = Math.floor(timeNum / 60);
			const seconds = Math.floor(timeNum % 60);
			formattedTime = `${minutes}m ${seconds}s`;
		} else {
			formattedTime = timePart;
		}

		// Extract the current run number from runPart
		const currentRun = runPart ? runPart.replace('Run ', '') : '';

		// Combine with the total runCount if available
		if (currentRun && runCount !== undefined) {
			return `${formattedTime} - Run ${currentRun} of ${runCount}`;
		}

		return formattedTime;
	};

	const truncateUrl = (url: string) => {
		if (url.length > 50) {
			return url.substring(0, 47) + '...';
		}
		return url;
	};

	return (
		<div className='space-y-4'>
			<div className='flex items-center gap-4 flex-wrap'>
				<Badge
					className={`${status ? getStatusColor(status) : 'bg-sky-500'} text-white px-6 py-2 text-base`}
				>
					{status || 'NO CURRENT ACTIVITY'}
				</Badge>

				{message && (
					<p className='text-lg text-white'>
						{formatMessage(message)}
					</p>
				)}

				{urlsRan !== undefined && (
					<span className='px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium'>
						{urlsRan} URLs processed
					</span>
				)}

				{timestamp && (
					<span className='text-md text-purple-500 ml-auto mr-4'>
						{formatTimestamp(timestamp)}
					</span>
				)}
			</div>

			{urls && urls.length > 0 && (
				<div className='mt-2'>
					<Button
						variant='ghost'
						onClick={() => setShowUrls(!showUrls)}
						className='flex items-center gap-2 text-purple-500 hover:text-purple-700'
					>
						<motion.div
							animate={{ rotate: showUrls ? 180 : 0 }}
							transition={{ duration: 0.2 }}
						>
							<ChevronDown />
						</motion.div>
						{showUrls ? 'Hide URLs' : 'Show URLs'}
					</Button>

					<AnimatePresence>
						{showUrls && (
							<motion.div
								initial={{ height: 0, opacity: 0 }}
								animate={{ height: 'auto', opacity: 1 }}
								exit={{ height: 0, opacity: 0 }}
								transition={{ duration: 0.2 }}
								className='overflow-hidden'
							>
								<div className='space-y-2 mt-2'>
									{urls.map((url, idx) => (
										<div
											key={idx}
											className='p-3 bg-gray-800/50 rounded-lg border border-gray-700'
										>
											<div className='flex items-center gap-2'>
												<span className='px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800'>
													{url.platform.toUpperCase()}
												</span>
												<a
													href={url.url}
													target='_blank'
													rel='noopener noreferrer'
													className='text-blue-400 hover:text-blue-300 text-sm font-mono underline'
												>
													{truncateUrl(url.url)}
												</a>
											</div>
										</div>
									))}
								</div>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			)}
		</div>
	);
}
